import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import api from './services/api';
import ToolsPage from './components/ToolsPage';
import EventList from './components/EventList';
import EventDetails from './components/EventDetails';
import DashboardsList from './components/DashboardsList';
import DashboardViewer from './components/DashboardViewer';
import EkinextList from './components/EkinextList';  // NUOVO
import EkinextDetails from './components/EkinextDetails';  // NUOVO
import PrivateRoute from './components/PrivateRoute';
import Login from './components/Login';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [errorEvents, setErrorEvents] = useState('');

  useEffect(() => {
    console.log('🔄 Esecuzione checkSession iniziale');
    setSessionLoading(true);
    api.checkSession()
      .then(data => {
        console.log('🔍 checkSession response (initial):', data);
        if (data.logged && data.user) {
          setUser({
            id: data.user.id,
            username: data.user.username,
            role: data.user.role,
            area_name: data.user.area_name
          });
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
        setSessionLoading(false);
      })
      .catch(err => {
        console.error('❌ checkSession error:', err);
        setUser(null);
        setIsAuthenticated(false);
        setSessionLoading(false);
      });
  }, []);

  const fetchAndSetEvents = useCallback(() => {
    setLoadingEvents(true);
    setErrorEvents('');
    api.getEvents()
      .then(data => {
        if (Array.isArray(data.events)) {
          setEvents(data.events);
        } else if (Array.isArray(data)) {
          setEvents(data);
        } else {
          setEvents([]);
        }
      })
      .catch(() => {
        setErrorEvents('Errore caricamento eventi');
        setEvents([]);
      })
      .finally(() => setLoadingEvents(false));
  }, []);

  const handleLogin = useCallback((loginResponse) => {
    console.log('🔍 Login response:', loginResponse);
    if (loginResponse.success && loginResponse.user) {
      setUser({
        id: loginResponse.user.id,
        username: loginResponse.user.username,
        role: loginResponse.user.role,
        area_name: loginResponse.user.area_name
      });
      setIsAuthenticated(true);
      fetchAndSetEvents();
    } else {
      alert('Errore login');
    }
  }, [fetchAndSetEvents]);

  const handleLogout = useCallback(() => {
    api.logout().finally(() => {
      setUser(null);
      setIsAuthenticated(false);
      setEvents([]);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAndSetEvents();
    } else {
      setEvents([]);
    }
  }, [isAuthenticated, fetchAndSetEvents]);

  if (sessionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <span className="ms-3">Verifica sessione in corso...</span>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Route pubblica: Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/tools" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />

        {/* Route protette */}
        <Route
          path="/tools"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <ToolsPage user={user} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        <Route
          path="/event-manager"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'AM', 'viewer']} userRole={user?.role}>
              <EventList user={user} events={events} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        <Route
          path="/event-manager/:id"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'AM', 'viewer']} userRole={user?.role}>
              <EventDetails user={user} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'viewer']} userRole={user?.role}>
              <DashboardsList user={user} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard/:name"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'viewer']} userRole={user?.role}>
              <DashboardViewer />
            </PrivateRoute>
          }
        />

        {/* NUOVE ROUTE EKINEXT */}
        <Route
          path="/ekinext"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin']} userRole={user?.role}>
              <EkinextList user={user} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        <Route
          path="/ekinext/:id"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin']} userRole={user?.role}>
              <EkinextDetails user={user} onLogout={handleLogout} />
            </PrivateRoute>
          }
        />

        {/* Redirect root */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/tools" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
